import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Copy, X, Loader2, Image as ImageIcon } from "lucide-react";

export default function AdminMedia() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/media");
      setItems(data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/media" replace />;
  if (user.role !== "admin") return (
    <div className="pt-40 container-lux min-h-[60vh]">
      <p className="overline text-muted-foreground">Restricted</p>
      <h1 className="mt-4 font-serif text-4xl">Admin access required</h1>
      <p className="mt-4 text-sm text-muted-foreground">Sign in with an atelier admin account to manage media.</p>
    </div>
  );

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) { fail++; toast.error(`${f.name}: ${formatErr(e)}`); }
    }
    setBusy(false);
    if (ok) toast.success(`Uploaded ${ok} file${ok>1?"s":""}${fail?` (${fail} failed)`:""}`);
    await load();
  };

  const softDelete = async (id) => {
    if (!confirm("Remove this file from the library?")) return;
    try { await api.delete(`/media/${id}`); setItems(items.filter(i => i.id !== id)); toast("Removed"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const copyUrl = async (url) => {
    const full = url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;
    try { await navigator.clipboard.writeText(full); toast.success("URL copied"); }
    catch { toast(full); }
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-media-page">
      <div className="container-lux">
        <p className="overline text-muted-foreground">Atelier · Admin</p>
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h1 className="font-serif tracking-tighter text-5xl md:text-6xl font-light">Media Library</h1>
          <p className="text-sm text-muted-foreground max-w-md">Upload campaign photography and product imagery. Each file returns a permanent URL you can paste into product records.</p>
        </div>

        <div
          className="mt-12 border-2 border-dashed border-border py-16 text-center bg-sand/40"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files || [])); }}
          data-testid="upload-zone"
        >
          <ImageIcon size={32} strokeWidth={0.6} className="mx-auto text-muted-foreground" />
          <p className="mt-4 font-serif text-2xl">Drop images here</p>
          <p className="mt-2 text-xs text-muted-foreground">JPG, PNG, WebP, AVIF, GIF, SVG, MP4 · up to 15 MB each</p>
          <button
            data-testid="upload-btn"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="lux-btn-dark mt-8 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} strokeWidth={1} />}
            {busy ? "Uploading…" : "Choose Files"}
          </button>
          <input
            ref={inputRef}
            data-testid="upload-input"
            type="file"
            multiple
            accept="image/*,video/mp4,application/pdf"
            className="hidden"
            onChange={(e) => upload(Array.from(e.target.files || []))}
          />
        </div>

        <div className="divider-thin my-12" />

        <div className="flex justify-between items-baseline">
          <p className="overline">Library · {items.length} {items.length === 1 ? "item" : "items"}</p>
          <button onClick={load} className="lux-link">Refresh</button>
        </div>

        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading library…</p>
        ) : items.length === 0 ? (
          <p className="mt-10 text-sm text-muted-foreground">No files yet — upload your first campaign photo above.</p>
        ) : (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map((it) => (
              <div key={it.id} className="group relative" data-testid={`media-${it.id}`}>
                <div className="aspect-square overflow-hidden bg-muted border border-border">
                  {it.content_type?.startsWith("image/") ? (
                    <img
                      src={`${API}/media/${it.id}`}
                      alt={it.original_filename}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground overline">
                      {it.content_type?.split("/")[1] || "FILE"}
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs truncate">{it.original_filename}</p>
                    <p className="text-[10px] text-muted-foreground">{formatSize(it.size)}</p>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`copy-${it.id}`} onClick={() => copyUrl(it.url)} className="p-2 hover:bg-muted" title="Copy URL"><Copy size={13} strokeWidth={1} /></button>
                    <button data-testid={`del-${it.id}`} onClick={() => softDelete(it.id)} className="p-2 hover:bg-muted" title="Remove"><X size={13} strokeWidth={1} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
